import type { CSSProperties, ReactNode } from 'react';

export type SortDir = 'asc' | 'desc';
export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/** Toggle helper for a sortable column: same key flips direction, new key starts descending. */
export function toggleSort<K extends string>(s: SortState<K>, key: K): SortState<K> {
  return s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' };
}

export function sortBy<T>(items: T[], pick: (t: T) => number | string, dir: SortDir): T[] {
  const m = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = pick(a);
    const y = pick(b);
    return (x < y ? -1 : x > y ? 1 : 0) * m;
  });
}

/** Table header cell; pass `sortKey` + `sort` + `onSort` to make it clickable with a chevron. */
export function SortTh<K extends string>({
  children,
  align = 'left',
  width,
  sortKey,
  sort,
  onSort,
}: {
  children: ReactNode;
  align?: CSSProperties['textAlign'];
  width?: string;
  sortKey?: K;
  sort?: SortState<K>;
  onSort?: (k: K) => void;
}) {
  const sortable = sortKey !== undefined && onSort !== undefined;
  const active = sortable && sort?.key === sortKey;
  const style: CSSProperties = {
    color: active ? '#fff' : 'var(--lv-brand-glow)',
    padding: '12px 16px',
    textAlign: align,
    fontWeight: 800,
    fontSize: 10,
    width,
    whiteSpace: 'nowrap',
  };
  if (!sortable) {
    return (
      <th className="label" style={style}>
        {children}
      </th>
    );
  }
  return (
    <th
      className="label"
      style={{ ...style, cursor: 'pointer', userSelect: 'none' }}
      aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        {children}
        <span aria-hidden style={{ opacity: active ? 1 : 0.4, fontSize: 9 }}>
          {active && sort?.dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}
